import { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import FileUpload from '../shared/FileUpload';
import { useApp } from '../../context';
import { useToast } from '../ui/Toast';
import { formatDate, safeUrl, currencySymbol } from '../../utils/format';

// Amounts default to sterling; pass a PO's currency for one raised in EUR/USD.
const fmt = (v, currency = 'GBP') => v != null
  ? `${currencySymbol(currency) || `${String(currency).toUpperCase()} `}${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : '—';

const STATUS_STYLE = {
  approved:  'bg-emerald-100 text-emerald-700',
  paid:      'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
  sent:      'bg-purple-100 text-purple-700',
  draft:     'bg-gray-100 text-gray-600',
};

// QA steps are admin-managed (Admin → Supplier QA Steps), not hard-coded here.
const QA_STATE = {
  not_required: { label: 'Not required', cls: 'bg-gray-100 text-gray-400', cell: '—',    cellCls: 'text-gray-300' },
  required:     { label: 'Outstanding',  cls: 'bg-gray-100 text-gray-600', cell: 'Req',  cellCls: 'text-gray-500' },
  requested:    { label: 'Requested',    cls: 'bg-blue-100 text-blue-700', cell: 'Sent', cellCls: 'text-blue-600' },
  received:     { label: 'Received',     cls: 'bg-amber-100 text-amber-700', cell: 'Rcvd', cellCls: 'text-amber-600' },
  verified:     { label: 'Verified',     cls: 'bg-green-100 text-green-700', cell: '✓',    cellCls: 'text-green-600 font-bold' },
  expired:      { label: 'Expired',      cls: 'bg-red-100 text-red-600',   cell: 'Exp',  cellCls: 'text-red-600 font-bold' },
};
const QA_STATUS_OPTIONS = ['required', 'requested', 'received', 'verified'];
const STATUS_LABEL = { required: 'Outstanding', requested: 'Requested', received: 'Received', verified: 'Verified' };

const todayStr = () => new Date().toISOString().slice(0, 10);
const effRequired = (step, doc) =>
  (doc && doc.required !== null && doc.required !== undefined) ? Number(doc.required) === 1 : Number(step.default_required) === 1;
function cellState(step, doc) {
  if (!effRequired(step, doc)) return 'not_required';
  if (!doc) return 'required';
  const expired = doc.expiry_date && doc.expiry_date < todayStr();
  if (doc.status === 'verified') return expired ? 'expired' : 'verified';
  if (doc.status === 'expired' || expired) return 'expired';
  return doc.status || 'required';
}
const shortLabel = (name) => {
  const map = {
    'Professional Indemnity Insurance': 'PI Ins.',
    'Public Liability Insurance': 'PL Ins.',
    'NDA / Confidentiality Agreement': 'NDA',
    'Master Services Agreement': 'Contract',
    'GDPR / Data Processing Agreement': 'GDPR',
    'Bank Details Verified': 'Bank',
  };
  if (map[name]) return map[name];
  return name.length > 12 ? name.split(/[\s/]+/).map(w => w[0]).join('').slice(0, 4).toUpperCase() : name;
};

// Compliance state derived from per-supplier required-step counts (supplied by the API).
function qaStatus(supplier) {
  const required = supplier.qa_required || 0;
  const expired  = supplier.qa_expired  || 0;
  const verified = supplier.qa_verified || 0;
  if (required === 0) return 'none';
  if (expired > 0)    return 'expired';
  if (verified >= required) return 'compliant';
  return 'partial';
}

function QaBadge({ supplier, small = false }) {
  const s = qaStatus(supplier);
  const cfg = {
    compliant: { cls: 'bg-green-100 text-green-700',  label: '✓ QA' },
    expired:   { cls: 'bg-red-100 text-red-700',      label: '✕ QA' },
    partial:   { cls: 'bg-amber-100 text-amber-700',  label: '⚠ QA' },
    none:      { cls: 'bg-gray-100 text-gray-400',    label: 'QA?' },
  }[s];
  return (
    <span className={`${small ? 'text-[7px]' : 'text-[8px]'} font-bold px-1.5 py-0.5 rounded ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

export default function SupplierPage() {
  const { toast } = useToast();
  const { navigate, detailId } = useApp();
  const rightPanelRef = useRef(null);
  const lastAutoId = useRef(null);
  const restoredRef = useRef(false);
  const [suppliers, setSuppliers] = useState([]);
  const [selected, setSelected]   = useState(null);
  const [pos, setPos]             = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [saving, setSaving]       = useState(false);
  const [loadingPos, setLoadingPos] = useState(false);
  const [contractors, setContractors]       = useState([]);
  const [contractorInvs, setContractorInvs] = useState([]);
  const [contractorsLoading, setContractorsLoading] = useState(false);
  const [hub, setHub]                       = useState([]);
  const [expandedWorker, setExpandedWorker] = useState(null);
  const [poPage, setPoPage]       = useState(1);
  const [poStatusFilter, setPoStatusFilter] = useState('');
  const [poSearch, setPoSearch]   = useState('');
  const PO_PAGE_SIZE = 15;
  const [deactivating, setDeactivating] = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [rightTab, setRightTab]   = useState('overview');
  const [view, setView]           = useState('suppliers'); // 'suppliers' | 'qa-dashboard'
  const [supplierTab, setSupplierTab] = useState('active'); // 'active' | 'inactive'
  const [qaData, setQaData]       = useState(null);        // { steps, suppliers, stats }
  const [qaLoading, setQaLoading] = useState(false);

  const load = async () => {
    const data = await api.get('suppliers');
    setSuppliers(data);
  };

  useEffect(() => { load(); }, []);

  // Auto-select supplier when navigated here from another page (e.g. External Resources → Manage QA)
  useEffect(() => {
    if (!detailId || suppliers.length === 0 || detailId === lastAutoId.current) return;
    const match = suppliers.find(s => s.id === detailId);
    if (!match) return;
    lastAutoId.current = detailId;
    document.querySelector('.content')?.scrollTo({ top: 0, behavior: 'instant' });
    setView('suppliers');
    setSupplierTab(match.is_active !== 0 ? 'active' : 'inactive');
    selectSupplier(match).then(() => setRightTab('qa'));
  }, [detailId, suppliers]); // eslint-disable-line

  // Restore the last-viewed supplier when returning to the page (no deep-link id).
  useEffect(() => {
    if (restoredRef.current || detailId || suppliers.length === 0) return;
    restoredRef.current = true;
    let savedId = null;
    try { savedId = localStorage.getItem('av_last_supplier'); } catch (e) { /* ignore */ }
    if (!savedId) return;
    const match = suppliers.find(s => s.id === savedId);
    if (match) { setView('suppliers'); setSupplierTab(match.is_active !== 0 ? 'active' : 'inactive'); selectSupplier(match); }
  }, [detailId, suppliers]); // eslint-disable-line

  const selectSupplier = async (s) => {
    rightPanelRef.current?.scrollTo({ top: 0, behavior: 'instant' });
    try { localStorage.setItem('av_last_supplier', s.id); } catch (e) { /* ignore */ }
    setSelected(s);
    setPos([]);
    setContractors([]);
    setContractorInvs([]);
    setHub([]);
    setExpandedWorker(null);
    setPoPage(1);
    setPoStatusFilter('');
    setPoSearch('');
    setRightTab('overview');
    setLoadingPos(true);
    try {
      const data = await api.get('purchase-orders', { supplier_id: s.id });
      setPos(data);
    } catch (_) {}
    setLoadingPos(false);
    setContractorsLoading(true);
    Promise.all([
      api.get(`suppliers/${s.id}/contractors`),
      api.get(`suppliers/${s.id}/invoices`),
      api.get(`suppliers/${s.id}/resource-hub`).catch(() => []),
    ]).then(([ctrs, invs, hubData]) => {
      setContractors(ctrs || []);
      setContractorInvs(invs || []);
      setHub(hubData || []);
    }).catch(() => {}).finally(() => setContractorsLoading(false));
  };

  const openQaDashboard = async () => {
    setView('qa-dashboard');
    setSelected(null);
    setQaLoading(true);
    try {
      const data = await api.get('suppliers/qa-dashboard');
      setQaData(data || null);
    } catch (_) {}
    setQaLoading(false);
  };

  const save = async (form) => {
    setSaving(true);
    try {
      await api.put(`suppliers/${form.id}`, form);
      setEditing(null);
      await load();
      setSelected(form);
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSaving(false);
  };

  const create = async (form) => {
    setSaving(true);
    try {
      await api.post('suppliers', form);
      setShowCreate(false);
      await load();
    } catch (e) { toast.error('Failed: ' + e.message); }
    setSaving(false);
  };

  const toggleActive = async () => {
    if (!selected) return;
    setDeactivating(true);
    try {
      const updated = await api.put(`suppliers/${selected.id}`, { is_active: selected.is_active === 0 ? 1 : 0 });
      setSelected(updated);
      await load();
    } catch (e) { toast.error('Failed: ' + e.message); }
    setDeactivating(false);
  };

  const deleteSupplier = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await api.delete(`suppliers/${selected.id}`);
      setSelected(null);
      setShowDeleteConfirm(false);
      await load();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
    setDeleting(false);
  };

  // Cancelled POs are not committed spend — exclude them from the totals.
  const livePos        = pos.filter(p => !['cancelled'].includes(p.status));
  const activePOs      = livePos.length;

  // Split by currency: PO amounts are never converted, so a supplier billing in
  // EUR gets its own committed/invoiced figures rather than being added to GBP.
  const poTotals = (() => {
    const byCur = new Map();
    for (const p of livePos) {
      const code = (p.currency || 'GBP').toUpperCase();
      const t = byCur.get(code) || { currency: code, value: 0, invoiced: 0 };
      t.value    += p.total_value || 0;
      t.invoiced += p.total_invoiced || 0;
      byCur.set(code, t);
    }
    return [...byCur.values()].sort((a, b) => (a.currency === 'GBP' ? -1 : b.currency === 'GBP' ? 1 : b.value - a.value));
  })();

  // QA summary stats across all suppliers
  const compliantCount = suppliers.filter(s => qaStatus(s) === 'compliant').length;
  const expiredCount   = suppliers.filter(s => qaStatus(s) === 'expired').length;
  const partialCount   = suppliers.filter(s => qaStatus(s) === 'partial').length;
  const noneCount      = suppliers.filter(s => qaStatus(s) === 'none').length;
  const activeCount    = suppliers.filter(s => s.is_active !== 0).length;

  return (
    <div className="fade-in flex gap-3" style={{ minHeight: 0, height: '100%' }}>
      {/* Left panel — supplier list */}
      <div className="w-60 flex-shrink-0 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-base font-extrabold text-navy">Suppliers</h2>
            <div className="text-[9px] text-gray-500">{activeCount} active</div>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="px-2 py-1 bg-navy text-white text-[9px] font-semibold rounded hover:bg-navy/80"
          >
            + Add
          </button>
        </div>

        {/* Active / Inactive tab strip */}
        <div className="flex mb-2 border border-gray-200 rounded overflow-hidden text-[9px] font-semibold">
          {[['active', 'Active', activeCount], ['inactive', 'Inactive', suppliers.filter(s => s.is_active === 0).length]].map(([key, label, count]) => (
            <button key={key} onClick={() => setSupplierTab(key)}
              className={`flex-1 py-1 flex items-center justify-center gap-1 transition-colors ${supplierTab === key ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
              {label}
              {count > 0 && <span className={`px-1 rounded ${supplierTab === key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>{count}</span>}
            </button>
          ))}
        </div>

        {/* QA summary strip */}
        {activeCount > 0 && (
          <button
            onClick={openQaDashboard}
            className={`mb-2 w-full flex items-center justify-between px-2.5 py-1.5 rounded border text-[8px] transition-colors ${view === 'qa-dashboard' ? 'bg-navy text-white border-navy' : 'bg-white border-gray-200 hover:bg-gold/5'}`}
          >
            <span className="font-bold">QA Dashboard</span>
            <span className="flex gap-1">
              {compliantCount > 0 && <span className={`px-1.5 py-0.5 rounded font-bold ${view === 'qa-dashboard' ? 'bg-green-500 text-green-950' : 'bg-green-100 text-green-700'}`}>{compliantCount} ✓</span>}
              {partialCount  > 0 && <span className={`px-1.5 py-0.5 rounded font-bold ${view === 'qa-dashboard' ? 'bg-amber-400 text-amber-950' : 'bg-amber-100 text-amber-700'}`}>{partialCount} ⚠</span>}
              {expiredCount  > 0 && <span className={`px-1.5 py-0.5 rounded font-bold ${view === 'qa-dashboard' ? 'bg-red-500 text-white' : 'bg-red-100 text-red-700'}`}>{expiredCount} ✕</span>}
              {noneCount     > 0 && <span className={`px-1.5 py-0.5 rounded font-bold ${view === 'qa-dashboard' ? 'bg-gray-400 text-gray-950' : 'bg-gray-100 text-gray-500'}`}>{noneCount} ?</span>}
            </span>
          </button>
        )}

        <div className="space-y-1 overflow-y-auto flex-1">
          {suppliers.filter(s => supplierTab === 'active' ? s.is_active !== 0 : s.is_active === 0).map(s => {
            const isInactive = s.is_active === 0;
            const isActive = selected?.id === s.id && view === 'suppliers';
            return (
              <button
                key={s.id}
                onClick={() => { setView('suppliers'); selectSupplier(s); }}
                className={`w-full text-left px-3 py-2 rounded border text-[10px] transition-colors ${
                  isActive
                    ? 'bg-navy text-white border-navy'
                    : isInactive
                      ? 'bg-gray-50 border-gray-200 hover:bg-gray-100 opacity-60'
                      : 'bg-white border-gray-200 hover:bg-gold/5'
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold truncate flex-1">{s.name}</span>
                  {isInactive && <span className="text-[7px] bg-red-100 text-red-500 font-bold px-1 rounded shrink-0">Inactive</span>}
                  {!isActive && <QaBadge supplier={s} small />}
                </div>
                {s.code && (
                  <div className={`text-[8px] font-mono ${isActive ? 'text-white/60' : 'text-gray-400'}`}>
                    {s.code}
                  </div>
                )}
                {s.contractor_count > 0 && (
                  <span className={`text-[8px] ${isActive ? 'text-white/60' : 'text-gray-400'}`}>{s.contractor_count} contractor{s.contractor_count !== 1 ? 's' : ''}</span>
                )}
                {s.supplies_resources ? (
                  <span className={`ml-1 text-[7px] font-bold px-1 py-0.5 rounded ${isActive ? 'bg-white/20 text-white' : 'bg-indigo-100 text-indigo-700'}`}>RESOURCES</span>
                ) : null}
              </button>
            );
          })}
          {suppliers.filter(s => supplierTab === 'active' ? s.is_active !== 0 : s.is_active === 0).length === 0 && (
            <p className="text-[10px] text-gray-400 text-center py-8">{supplierTab === 'active' ? 'No active suppliers' : 'No inactive suppliers'}</p>
          )}
        </div>
      </div>

      {/* Right panel */}
      <div ref={rightPanelRef} className="flex-1 min-w-0 overflow-y-auto">
        {/* ── QA Dashboard view ── */}
        {view === 'qa-dashboard' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-extrabold text-navy">Supplier QA Compliance</h3>
              <button onClick={() => setView('suppliers')} className="text-[9px] text-gray-400 hover:text-gray-600">← Back to Suppliers</button>
            </div>

            {!qaData || qaLoading ? (
              <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div>
            ) : (() => {
              const st = qaData.stats || {};
              const steps = qaData.steps || [];
              const rows = qaData.suppliers || [];
              return (
                <>
                  {/* Compliance summary */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
                    {[
                      { label: 'Active Suppliers', count: st.total || 0,     cls: 'bg-navy/5 border-navy/20 text-navy' },
                      { label: 'Fully Compliant',  count: st.compliant || 0, cls: 'bg-green-50 border-green-200 text-green-700' },
                      { label: 'In Progress',      count: st.partial || 0,   cls: 'bg-amber-50 border-amber-200 text-amber-700' },
                      { label: 'Expired',          count: st.expired || 0,   cls: 'bg-red-50 border-red-200 text-red-700' },
                      { label: 'Not Started',      count: st.none || 0,      cls: 'bg-gray-50 border-gray-200 text-gray-500' },
                    ].map(c => (
                      <div key={c.label} className={`border rounded p-3 text-center ${c.cls}`}>
                        <div className="text-2xl font-extrabold">{c.count}</div>
                        <div className="text-[9px] font-semibold mt-0.5">{c.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Usage analytics — suppliers with any PO / invoice / contract activity in the window */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
                    {[
                      { label: 'Used in last 3 months',  count: st.used_3m || 0 },
                      { label: 'Used in last 6 months',  count: st.used_6m || 0 },
                      { label: 'Used in last 12 months', count: st.used_12m || 0 },
                    ].map(c => (
                      <div key={c.label} className="border border-gray-200 rounded p-3 text-center bg-white">
                        <div className="text-xl font-extrabold text-navy">{c.count}<span className="text-[10px] text-gray-400 font-semibold"> / {st.total || 0}</span></div>
                        <div className="text-[9px] font-semibold text-gray-500 mt-0.5">{c.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Compliance matrix (columns are admin-managed QA steps) */}
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-[9px]">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left px-3 py-2 font-bold text-gray-600 border-b border-gray-200 min-w-[160px]">Supplier</th>
                          {steps.map(step => (
                            <th key={step.id} title={step.name} className="text-center px-2 py-2 font-bold text-gray-500 border-b border-gray-200 min-w-[64px]">
                              {shortLabel(step.name)}
                            </th>
                          ))}
                          <th className="text-center px-2 py-2 font-bold text-gray-500 border-b border-gray-200">Last used</th>
                          <th className="text-center px-2 py-2 font-bold text-gray-500 border-b border-gray-200">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(supplier => (
                          <tr key={supplier.id} className="border-b border-gray-100 hover:bg-gold/5">
                            <td className="px-3 py-2 font-semibold text-navy">
                              <button onClick={() => { const full = suppliers.find(s => s.id === supplier.id); setView('suppliers'); selectSupplier(full || supplier); setRightTab('qa'); }}
                                className="hover:underline text-left">{supplier.name}</button>
                            </td>
                            {steps.map(step => {
                              const doc = supplier.documents?.[step.id];
                              const stt = cellState(step, doc);
                              const cfg = QA_STATE[stt];
                              return (
                                <td key={step.id} className="px-2 py-2 text-center">
                                  <span className={`text-[8px] ${cfg.cellCls}`}>{cfg.cell}</span>
                                  {doc?.expiry_date && stt !== 'expired' && stt !== 'not_required' && (
                                    <div className="text-[7px] text-gray-400">{doc.expiry_date.slice(2)}</div>
                                  )}
                                </td>
                              );
                            })}
                            <td className="px-2 py-2 text-center text-[8px] text-gray-500">{supplier.last_activity ? formatDate(supplier.last_activity) : <span className="text-gray-300">—</span>}</td>
                            <td className="px-2 py-2 text-center">
                              <QaBadge supplier={{ qa_required: supplier.compliance?.required || 0, qa_verified: supplier.compliance?.verified || 0, qa_expired: supplier.compliance?.expired || 0 }} />
                            </td>
                          </tr>
                        ))}
                        {rows.length === 0 && (
                          <tr><td colSpan={steps.length + 3} className="text-center py-8 text-gray-400">No active suppliers found.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ── Supplier detail view ── */}
        {view === 'suppliers' && !selected && (
          <div className="flex items-center justify-center h-48 text-[10px] text-gray-400">
            ← Select a supplier to view details and purchase orders
          </div>
        )}

        {view === 'suppliers' && selected && (
          <div>
            {selected.is_active === 0 && (
              <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded flex items-center justify-between">
                <span className="text-[9px] text-red-700 font-semibold">⚠ This supplier is deactivated and will not appear in purchase order or invoice workflows.</span>
              </div>
            )}

            {/* Supplier card */}
            <div className="bg-white border border-gray-200 rounded p-3 mb-3">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-extrabold text-navy">{selected.name}</h3>
                    {selected.is_active === 0 && <span className="text-[8px] bg-red-100 text-red-600 font-bold px-1.5 py-0.5 rounded">Inactive</span>}
                    <QaBadge supplier={selected} />
                  </div>
                  {selected.code && (
                    <div className="text-[9px] text-gray-400 font-mono mt-0.5">{selected.code}</div>
                  )}
                  <div className="mt-2 grid grid-cols-2 gap-x-8 gap-y-1 text-[9px] text-gray-600">
                    {selected.contact_name  && <div><span className="text-gray-400">Contact: </span>{selected.contact_name}</div>}
                    {selected.contact_email && (
                      <div>
                        <span className="text-gray-400">Email: </span>
                        <a href={`mailto:${selected.contact_email}`} className="text-gold hover:underline">{selected.contact_email}</a>
                      </div>
                    )}
                    {selected.contact_phone && <div><span className="text-gray-400">Phone: </span>{selected.contact_phone}</div>}
                    {selected.account_number && <div><span className="text-gray-400">Account #: </span>{selected.account_number}</div>}
                    {selected.payment_terms && <div><span className="text-gray-400">Terms: </span>{selected.payment_terms}</div>}
                    {safeUrl(selected.website) && (
                      <div>
                        <span className="text-gray-400">Website: </span>
                        <a href={safeUrl(selected.website)} target="_blank" rel="noreferrer" className="text-gold hover:underline truncate">{selected.website}</a>
                      </div>
                    )}
                    {selected.address && (
                      <div className="col-span-2"><span className="text-gray-400">Address: </span>{selected.address}</div>
                    )}
                    {selected.notes && (
                      <div className="col-span-2 mt-1 p-2 bg-gray-50 rounded text-[8px] italic text-gray-500">{selected.notes}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 ml-4 flex-shrink-0">
                  <button
                    onClick={() => setEditing({ ...selected })}
                    className="px-2 py-1 text-[9px] font-semibold bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                  >
                    Edit
                  </button>
                  {selected.is_active !== 0 ? (
                    <button
                      onClick={toggleActive}
                      disabled={deactivating}
                      className="px-2 py-1 text-[9px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded hover:bg-amber-100 disabled:opacity-40"
                    >
                      {deactivating ? '…' : 'Deactivate'}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={toggleActive}
                        disabled={deactivating}
                        className="px-2 py-1 text-[9px] font-semibold bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 disabled:opacity-40"
                      >
                        {deactivating ? '…' : 'Reactivate'}
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="px-2 py-1 text-[9px] font-semibold bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* PO summary stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-100">
                <div className="text-center">
                  <div className="text-[8px] text-gray-500 uppercase font-bold">Total POs</div>
                  <div className="text-xl font-extrabold text-navy">{pos.length}</div>
                  <div className="text-[8px] text-gray-400">{activePOs} active</div>
                </div>
                <div className="text-center">
                  <div className="text-[8px] text-gray-500 uppercase font-bold">Total Committed</div>
                  {poTotals.length === 0 && <div className="text-xl font-extrabold text-navy">{fmt(0)}</div>}
                  {poTotals.map((t, i) => (
                    <div key={t.currency} className={i === 0 ? 'text-xl font-extrabold text-navy' : 'text-[11px] font-bold text-gray-500'}>
                      {fmt(t.value, t.currency)}
                    </div>
                  ))}
                  <div className="text-[8px] text-gray-400">across all POs</div>
                </div>
                <div className="text-center">
                  <div className="text-[8px] text-gray-500 uppercase font-bold">Total Invoiced</div>
                  {poTotals.length === 0 && <div className="text-xl font-extrabold text-amber-600">{fmt(0)}</div>}
                  {poTotals.map((t, i) => (
                    <div key={t.currency}>
                      <div className={`${i === 0 ? 'text-xl font-extrabold' : 'text-[11px] font-bold'} ${t.invoiced > t.value ? 'text-red-500' : 'text-amber-600'}`}>
                        {fmt(t.invoiced, t.currency)}
                      </div>
                      <div className="text-[8px] text-gray-400">{fmt(t.value - t.invoiced, t.currency)} remaining</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-3">
              {[['overview', 'Purchase Orders'], ['hub', 'Resource Hub'], ['contractors', 'Contractors'], ['qa', 'QA Documents']].map(([id, label]) => (
                <button key={id} onClick={() => setRightTab(id)}
                  className={`px-4 py-2 text-[9px] font-semibold border-b-2 transition-colors ${rightTab === id ? 'border-navy text-navy' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
                  {label}
                  {id === 'hub' && hub.length > 0 && (
                    <span className="ml-1 bg-gray-200 text-gray-600 text-[7px] px-1 py-0.5 rounded-full">{hub.length}</span>
                  )}
                  {id === 'qa' && (
                    <span className="ml-1.5"><QaBadge supplier={selected} small /></span>
                  )}
                </button>
              ))}
            </div>

            {/* ── Overview (PO table) ── */}
            {rightTab === 'overview' && (
              <div className="bg-white border border-gray-200 rounded overflow-x-auto">
                <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-[9px] font-bold uppercase text-gray-500">
                    Purchase Orders
                    {pos.length > 0 && <span className="ml-1 font-normal text-gray-400">({pos.length})</span>}
                  </span>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Search PO…"
                      value={poSearch}
                      onChange={e => { setPoSearch(e.target.value); setPoPage(1); }}
                      className="border border-gray-200 rounded px-2 py-0.5 text-[8px] w-28 focus:outline-none focus:border-gold"
                    />
                    <select
                      value={poStatusFilter}
                      onChange={e => { setPoStatusFilter(e.target.value); setPoPage(1); }}
                      className="border border-gray-200 rounded px-1.5 py-0.5 text-[8px] focus:outline-none focus:border-gold"
                    >
                      <option value="">All statuses</option>
                      {[...new Set(pos.map(p => p.status).filter(Boolean))].map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    {pos.length > 0 && (
                      <button
                        onClick={() => {
                          const wb = XLSX.utils.book_new();
                          const headers = ['PO #', 'SOW', 'Description', 'Status', 'Issued Date', 'Acknowledged Date', 'Work Scheduled', 'Currency', 'PO Value', 'Invoiced', 'Notes'];
                          const rows = pos.map(p => [
                            p.po_number, p.sow_reference || '', p.description || p.notes || '',
                            p.status, p.order_date || '', p.acknowledged_at?.slice(0,10) || '',
                            p.work_scheduled_date || '', (p.currency || 'GBP').toUpperCase(),
                            p.total_value || 0, p.total_invoiced || 0, p.notes || '',
                          ]);
                          const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
                          ws['!cols'] = [14,12,30,14,12,14,14,10,12,12,30].map(w => ({ wch: w }));
                          XLSX.utils.book_append_sheet(wb, ws, 'POs');
                          XLSX.writeFile(wb, `${selected.name.replace(/\s+/g,'-')}-pos-${new Date().toISOString().slice(0,10)}.xlsx`);
                        }}
                        className="px-2 py-0.5 text-[8px] font-semibold border border-gray-200 rounded bg-white hover:bg-gray-50"
                      >Export</button>
                    )}
                  </div>
                </div>
                {loadingPos ? (
                  <div className="text-center py-6 text-[10px] text-gray-400">Loading...</div>
                ) : (() => {
                  const filtered = pos.filter(po => {
                    if (poStatusFilter && po.status !== poStatusFilter) return false;
                    if (poSearch) {
                      const q = poSearch.toLowerCase();
                      return (
                        (po.po_number || '').toLowerCase().includes(q) ||
                        (po.sow_reference || '').toLowerCase().includes(q) ||
                        (po.description || '').toLowerCase().includes(q) ||
                        (po.notes || '').toLowerCase().includes(q)
                      );
                    }
                    return true;
                  });
                  const totalPages = Math.max(1, Math.ceil(filtered.length / PO_PAGE_SIZE));
                  const page = Math.min(poPage, totalPages);
                  const pagePos = filtered.slice((page - 1) * PO_PAGE_SIZE, page * PO_PAGE_SIZE);
                  return (
                    <>
                      <div className="overflow-x-auto"><table className="w-full">
                        <thead>
                          <tr>
                            <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">PO #</th>
                            <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">SOW</th>
                            <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Description</th>
                            <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Status</th>
                            <th className="px-2 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">PO Value</th>
                            <th className="px-2 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Invoiced</th>
                            <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Issued Date</th>
                            <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Accepted Date</th>
                            <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Work Scheduled</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagePos.map(po => (
                            <tr key={po.id}
                              className="border-b border-gray-100 hover:bg-gold/5 cursor-pointer"
                              onClick={() => navigate('pos', po.id)}>
                              <td className="px-2 py-1.5 text-[9px] font-mono font-semibold text-navy">{po.po_number}</td>
                              <td className="px-2 py-1.5 text-[9px] text-gray-600">{po.sow_reference || '—'}</td>
                              <td className="px-2 py-1.5 text-[9px] max-w-[200px]">
                                <div className="truncate">{po.description || po.notes || '—'}</div>
                              </td>
                              <td className="px-2 py-1.5">
                                <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded capitalize ${STATUS_STYLE[po.status] || STATUS_STYLE.draft}`}>
                                  {po.status}
                                </span>
                              </td>
                              <td className="px-2 py-1.5 text-[9px] font-semibold text-right">{fmt(po.total_value, po.currency)}</td>
                              <td className={`px-2 py-1.5 text-[9px] font-semibold text-right ${po.total_invoiced > po.total_value ? 'text-red-500' : ''}`}>
                                {fmt(po.total_invoiced, po.currency)}
                              </td>
                              <td className="px-2 py-1.5 text-[9px] text-gray-400">{formatDate(po.order_date)}</td>
                              <td className="px-2 py-1.5 text-[9px] text-gray-400">{po.acknowledged_at ? formatDate(po.acknowledged_at) : '—'}</td>
                              <td className="px-2 py-1.5 text-[9px] text-gray-400">{po.work_scheduled_date ? formatDate(po.work_scheduled_date) : '—'}</td>
                            </tr>
                          ))}
                          {filtered.length === 0 && (
                            <tr>
                              <td colSpan={9} className="text-center py-8 text-[10px] text-gray-400">
                                {pos.length === 0 ? 'No purchase orders for this supplier' : 'No POs match your filter'}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table></div>
                      {totalPages > 1 && (
                        <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-between">
                          <span className="text-[8px] text-gray-400">
                            Showing {(page - 1) * PO_PAGE_SIZE + 1}–{Math.min(page * PO_PAGE_SIZE, filtered.length)} of {filtered.length}
                          </span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setPoPage(p => Math.max(1, p - 1))}
                              disabled={page === 1}
                              className="px-2 py-0.5 text-[8px] border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50"
                            >← Prev</button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).filter(p =>
                              p === 1 || p === totalPages || Math.abs(p - page) <= 1
                            ).reduce((acc, p, idx, arr) => {
                              if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                              acc.push(p);
                              return acc;
                            }, []).map((p, i) =>
                              p === '…' ? (
                                <span key={`e${i}`} className="text-[8px] text-gray-400 px-1">…</span>
                              ) : (
                                <button
                                  key={p}
                                  onClick={() => setPoPage(p)}
                                  className={`w-6 h-5 text-[8px] rounded border ${page === p ? 'bg-navy text-white border-navy' : 'border-gray-200 hover:bg-gray-50'}`}
                                >{p}</button>
                              )
                            )}
                            <button
                              onClick={() => setPoPage(p => Math.min(totalPages, p + 1))}
                              disabled={page === totalPages}
                              className="px-2 py-0.5 text-[8px] border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50"
                            >Next →</button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* ── Resource Hub (supplier → contractor → contracts/SOWs + onboarding + QA) ── */}
            {rightTab === 'hub' && (() => {
              const CSTAT = { draft:'bg-gray-100 text-gray-600', issued:'bg-blue-100 text-blue-700', active:'bg-green-100 text-green-700', completed:'bg-purple-100 text-purple-700', cancelled:'bg-red-100 text-red-600' };
              const qaVerified = selected.qa_verified || 0, qaTotal = selected.qa_required || 0, qaExpired = selected.qa_expired || 0;
              return (
                <div>
                  {!selected.supplies_resources && (
                    <div className="mb-3 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded text-[9px] text-indigo-700">
                      This supplier isn't flagged as supplying resources. Tick “supplies contract resources” in Edit to mark it as a resource provider.
                    </div>
                  )}
                  {/* Supplier-level QA summary sits at the top of the tree */}
                  <div className="flex items-center gap-2 mb-3 text-[9px]">
                    <span className="font-bold text-gray-500 uppercase tracking-wide">Supplier QA</span>
                    <span className={`px-1.5 py-0.5 rounded font-semibold ${qaExpired > 0 ? 'bg-red-100 text-red-700' : qaVerified >= qaTotal && qaTotal > 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {qaVerified}/{qaTotal} verified{qaExpired > 0 ? ` · ${qaExpired} expired` : ''}
                    </span>
                    <button onClick={() => setRightTab('qa')} className="text-gold hover:underline">manage →</button>
                  </div>
                  {hub.length === 0 ? (
                    <div className="text-[9px] text-gray-400 italic py-4">No contractors linked to this supplier yet. Link them from the contractor's record on the Contractor Dashboard.</div>
                  ) : (
                    <div className="space-y-1.5">
                      {hub.map(w => {
                        const onbPct = w.onboarding.total ? Math.round((w.onboarding.complete / w.onboarding.total) * 100) : 0;
                        const open = expandedWorker === w.id;
                        return (
                          <div key={w.id} className="border border-gray-200 rounded-lg overflow-hidden">
                            <button onClick={() => setExpandedWorker(open ? null : w.id)}
                              className="w-full flex items-center gap-3 px-3 py-2 bg-gray-50/60 hover:bg-gray-50 text-left">
                              <span className="text-gray-400 text-[10px]">{open ? '▾' : '▸'}</span>
                              <span className="text-[11px] font-semibold text-navy">{w.first_name} {w.last_name}</span>
                              {w.email && <span className="text-[8px] text-gray-400">{w.email}</span>}
                              <span className={`text-[7px] font-bold px-1 py-0.5 rounded ${w.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{w.is_active ? 'Active' : 'Inactive'}</span>
                              <span className="ml-auto flex items-center gap-2">
                                <span className="text-[8px] text-gray-500">{w.contracts.length} contract{w.contracts.length !== 1 ? 's' : ''}</span>
                                <span className="flex items-center gap-1" title="Onboarding complete">
                                  <span className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                    <span className={`block h-full ${onbPct === 100 ? 'bg-green-500' : 'bg-amber-400'}`} style={{ width: `${onbPct}%` }} />
                                  </span>
                                  <span className="text-[8px] text-gray-500">{w.onboarding.complete}/{w.onboarding.total}</span>
                                </span>
                              </span>
                            </button>
                            {open && (
                              <div className="px-3 py-2 border-t border-gray-100">
                                {w.contracts.length === 0 ? (
                                  <div className="text-[8px] text-gray-400 italic">No contracts.</div>
                                ) : (
                                  <div className="overflow-x-auto"><table className="w-full text-[9px]">
                                    <thead><tr className="text-gray-400 uppercase text-[7px]">
                                      <th className="text-left py-1">Contract</th><th className="text-left py-1">SOW</th>
                                      <th className="text-left py-1">Client</th><th className="text-right py-1">Days</th>
                                      <th className="text-right py-1">Rate</th><th className="text-left py-1 pl-2">Status</th>
                                    </tr></thead>
                                    <tbody>
                                      {w.contracts.map(ct => (
                                        <tr key={ct.id} className="border-t border-gray-50">
                                          <td className="py-1 font-mono text-gray-600">{ct.reference}</td>
                                          <td className="py-1">{ct.sow_reference || <span className="text-gray-400 italic">{ct.overhead_category ? `overhead · ${ct.overhead_category}` : '—'}</span>}</td>
                                          <td className="py-1 text-gray-500">{ct.client_name || '—'}</td>
                                          <td className="py-1 text-right">{ct.agreed_days ?? '—'}</td>
                                          <td className="py-1 text-right">{ct.day_rate != null ? `£${ct.day_rate}` : '—'}</td>
                                          <td className="py-1 pl-2"><span className={`px-1.5 py-0.5 rounded font-semibold ${CSTAT[ct.status] || 'bg-gray-100 text-gray-600'}`}>{ct.status}</span></td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table></div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── Contractors ── */}
            {rightTab === 'contractors' && (
              <div>
                {contractorsLoading ? (
                  <div className="text-[9px] text-gray-400 italic py-4">Loading…</div>
                ) : contractors.length === 0 ? (
                  <div className="text-[9px] text-gray-400 italic py-4">No contractor resources linked. Link them from the contractor's record on the Contractor Dashboard.</div>
                ) : (
                  <div className="space-y-1.5">
                    {contractors.map(c => (
                      <div key={c.id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-gray-100 bg-gray-50/60">
                        <div>
                          <span className="text-[11px] font-semibold text-navy">{c.first_name} {c.last_name}</span>
                          {c.email && <span className="text-[9px] text-gray-400 ml-2">{c.email}</span>}
                        </div>
                        <div className="flex items-center gap-3">
                          {c.pending_invoices > 0 && (
                            <span className="text-[8px] bg-amber-100 text-amber-700 font-semibold px-1.5 py-0.5 rounded">{c.pending_invoices} pending</span>
                          )}
                          <span className="text-[9px] font-semibold text-navy">£{(c.total_invoiced || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                          <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {c.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {contractorInvs.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-3">
                      Contractor Invoices <span className="ml-2 font-normal text-gray-400">({contractorInvs.length})</span>
                    </h3>
                    <div className="overflow-x-auto"><table className="w-full text-[9px]">
                      <thead>
                        <tr className="border-b border-gray-100 text-gray-400 font-semibold uppercase">
                          <th className="text-left py-1.5 px-2">Invoice</th>
                          <th className="text-left py-1.5 px-2">Contractor</th>
                          <th className="text-left py-1.5 px-2">Period</th>
                          <th className="text-right py-1.5 px-2">Amount</th>
                          <th className="text-left py-1.5 px-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contractorInvs.map(inv => (
                          <tr key={inv.id} className="border-b border-gray-50 hover:bg-gold/5">
                            <td className="py-1.5 px-2 font-semibold">{inv.invoice_number || '—'}</td>
                            <td className="py-1.5 px-2 text-gray-600">{inv.worker_name}</td>
                            <td className="py-1.5 px-2 text-gray-400">{inv.period_from ? `${inv.period_from} – ${inv.period_to || ''}` : '—'}</td>
                            <td className="py-1.5 px-2 text-right font-semibold">£{(inv.total_amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                            <td className="py-1.5 px-2">
                              <span className={`px-1.5 py-0.5 rounded font-semibold ${
                                inv.status === 'paid'         ? 'bg-purple-100 text-purple-700' :
                                inv.status === 'approved'     ? 'bg-green-100 text-green-700' :
                                inv.status === 'under_review' ? 'bg-amber-100 text-amber-700' :
                                inv.status === 'rejected'     ? 'bg-red-100 text-red-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>{inv.status.replace('_', ' ')}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                  </div>
                )}
              </div>
            )}

            {/* ── QA Documents ── */}
            {rightTab === 'qa' && (
              <SupplierQaTab supplierId={selected.id} onChanged={load} />
            )}
          </div>
        )}
      </div>

      {editing && (
        <SupplierModal
          title={`Edit: ${editing.name}`}
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={save}
          saving={saving}
        />
      )}

      {showCreate && (
        <SupplierModal
          title="Add Supplier"
          initial={{ name: '', code: '', company_number: '', vat_number: '', contact_name: '', contact_email: '', account_number: '', contact_phone: '', website: '', payment_terms: '', address: '', notes: '', supplies_resources: 0 }}
          onClose={() => setShowCreate(false)}
          onSave={create}
          saving={saving}
        />
      )}

      {showDeleteConfirm && selected && (
        <Modal title={<span className="text-red-700">Delete Supplier</span>} onClose={() => setShowDeleteConfirm(false)}>
            <p className="text-[10px] text-gray-600 mb-4">
              Are you sure you want to permanently delete <strong>{selected.name}</strong>?
              This cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-3 py-1.5 text-[10px] border border-gray-200 rounded bg-white hover:bg-gray-50"
              >Cancel</button>
              <button
                onClick={deleteSupplier}
                disabled={deleting}
                className="px-3 py-1.5 text-[10px] font-semibold bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-40"
              >{deleting ? 'Deleting…' : 'Yes, Delete'}</button>
            </div>
        </Modal>
      )}
    </div>
  );
}

function SupplierModal({ title, initial, onClose, onSave, saving }) {
  const [form, setForm] = useState({ ...initial });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <Modal title={title} onClose={onClose} footer={
      <>
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button
          onClick={() => onSave(form)}
          disabled={saving || !form.name?.trim()}
          className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40"
        >
          {saving ? 'Saving...' : title.startsWith('Add') ? 'Add Supplier' : 'Save Changes'}
        </button>
      </>
    }>
      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Supplier Name *</label>
          <input className="form-input" value={form.name || ''} onChange={e => set('name', e.target.value)} placeholder="e.g. Cisco Systems Ltd" autoFocus />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Company Number</label>
          <input className="form-input" value={form.company_number || ''} onChange={e => set('company_number', e.target.value)} placeholder="12345678" />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">VAT Number</label>
          <input className="form-input" value={form.vat_number || ''} onChange={e => set('vat_number', e.target.value)} placeholder="GB123456789" />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Supplier Code</label>
          <input className="form-input font-mono" value={form.code || ''} onChange={e => set('code', e.target.value)} placeholder="CISCO" />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Account Number</label>
          <input className="form-input" value={form.account_number || ''} onChange={e => set('account_number', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Contact Name</label>
          <input className="form-input" value={form.contact_name || ''} onChange={e => set('contact_name', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Contact Email</label>
          <input className="form-input" type="email" value={form.contact_email || ''} onChange={e => set('contact_email', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Phone</label>
          <input className="form-input" type="tel" value={form.contact_phone || ''} onChange={e => set('contact_phone', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Payment Terms</label>
          <input className="form-input" value={form.payment_terms || ''} onChange={e => set('payment_terms', e.target.value)} placeholder="Net 30" />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Website</label>
          <input className="form-input" type="url" value={form.website || ''} onChange={e => set('website', e.target.value)} placeholder="https://" />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Address</label>
          <input className="form-input" value={form.address || ''} onChange={e => set('address', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes</label>
          <textarea className="form-input" rows={2} value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="flex items-center gap-2 text-[10px] font-semibold text-gray-700">
            <input type="checkbox" className="accent-navy" checked={!!form.supplies_resources} onChange={e => set('supplies_resources', e.target.checked ? 1 : 0)} />
            This supplier supplies contract resources (contractors)
          </label>
          <div className="text-[8px] text-gray-400 mt-0.5 ml-5">Flags the supplier as a resource provider — its contractors and their contracts/SOWs are managed on the Contractor Dashboard.</div>
        </div>
      </div>
    </Modal>
  );
}

// Per-supplier QA: each admin-defined step can be required / not required for this
// supplier; required steps track status, expiry, an uploaded document, and who
// verified/updated them. All changes are attributed and audited server-side.
function SupplierQaTab({ supplierId, onChanged }) {
  const { toast } = useToast();
  const [qa, setQa]           = useState(null);
  const [loading, setLoading] = useState(true);
  const [buf, setBuf]         = useState({});      // stepId -> dirty field overrides
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setQa(await api.get(`suppliers/${supplierId}/qa`)); }
    catch { setQa({ compliance: { required: 0, verified: 0, expired: 0, outstanding: [] }, steps: [] }); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [supplierId]);

  const val = (step, k) => (buf[step.id] && k in buf[step.id]) ? buf[step.id][k] : step[k];
  const setField = (id, k, v) => setBuf(p => ({ ...p, [id]: { ...p[id], [k]: v } }));
  const clearBuf = (id) => setBuf(p => { const n = { ...p }; delete n[id]; return n; });

  const save = async (step, extra = {}) => {
    setSavingId(step.id);
    try {
      await api.put(`suppliers/${supplierId}/qa/${step.id}`, {
        required: val(step, 'required') ? 1 : 0,
        status: val(step, 'status') || 'required',
        expiry_date: val(step, 'expiry_date') || null,
        notes: val(step, 'notes') || null,
        ...extra,
      });
      clearBuf(step.id);
      await load();
      onChanged?.();
    } catch (e) { toast.error('Save failed: ' + (e.data?.error || e.message)); }
    setSavingId(null);
  };

  if (loading) return <div className="text-center py-8 text-[10px] text-gray-400">Loading QA…</div>;
  const steps = qa?.steps || [];
  const c = qa?.compliance || { required: 0, verified: 0, expired: 0, outstanding: [] };
  if (steps.length === 0) {
    return <div className="text-[9px] text-gray-400 italic py-6">No QA steps defined yet. Add them in <strong>Admin → Supplier QA Steps</strong>.</div>;
  }

  return (
    <div>
      {/* Compliance banner */}
      <div className={`mb-3 px-3 py-2 rounded border flex items-center justify-between gap-3 ${c.required === 0 ? 'bg-gray-50 border-gray-200' : c.expired > 0 ? 'bg-red-50 border-red-200' : c.verified >= c.required ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="text-[9px] font-semibold">
          {c.required === 0
            ? <span className="text-gray-500">No QA steps are marked required for this supplier.</span>
            : c.verified >= c.required && c.expired === 0
              ? <span className="text-green-700">✓ Fully compliant — {c.verified}/{c.required} required steps verified.</span>
              : <span className={c.expired > 0 ? 'text-red-700' : 'text-amber-700'}>{c.verified}/{c.required} required steps verified{c.expired > 0 ? ` · ${c.expired} expired` : ''}</span>}
        </div>
        {c.outstanding?.length > 0 && (
          <div className="text-[8px] text-gray-500 truncate max-w-[50%]" title={c.outstanding.join(', ')}>Outstanding: {c.outstanding.join(', ')}</div>
        )}
      </div>
      <p className="text-[8px] text-gray-400 mb-2">Mark each step required (or not) for this supplier; required steps must reach <strong>Verified</strong> to be compliant. Every change is recorded against your name.</p>

      <div className="space-y-2">
        {steps.map(step => {
          const isReq = !!val(step, 'required');
          const state = isReq
            ? cellState({ default_required: 1 }, { required: 1, status: val(step, 'status'), expiry_date: val(step, 'expiry_date') })
            : 'not_required';
          const cfg = QA_STATE[state];
          return (
            <div key={step.id} className={`border rounded p-2.5 ${isReq ? 'border-gray-200' : 'border-gray-100 bg-gray-50/50'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-navy">{step.name}</span>
                    <span className={`text-[7px] px-1.5 py-0.5 rounded font-semibold ${cfg.cls}`}>{cfg.label}</span>
                  </div>
                  {step.description && <div className="text-[8px] text-gray-400 mt-0.5">{step.description}</div>}
                </div>
                <label className="flex items-center gap-1 text-[8px] font-semibold text-gray-600 shrink-0">
                  <input type="checkbox" className="accent-navy" checked={isReq} onChange={e => setField(step.id, 'required', e.target.checked ? 1 : 0)} />
                  Required
                </label>
              </div>

              {isReq && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[7px] font-bold text-gray-400 uppercase mb-0.5">Status</label>
                    <select className="form-input text-[9px]" value={val(step, 'status') || 'required'} onChange={e => setField(step.id, 'status', e.target.value)}>
                      {QA_STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                  </div>
                  {!!step.tracks_expiry && (
                    <div>
                      <label className="block text-[7px] font-bold text-gray-400 uppercase mb-0.5">Expiry Date</label>
                      <input type="date" className="form-input text-[9px]" value={val(step, 'expiry_date') || ''} onChange={e => setField(step.id, 'expiry_date', e.target.value)} />
                    </div>
                  )}
                  <div className="col-span-2">
                    <label className="block text-[7px] font-bold text-gray-400 uppercase mb-0.5">Notes</label>
                    <input className="form-input text-[9px]" value={val(step, 'notes') || ''} onChange={e => setField(step.id, 'notes', e.target.value)} />
                  </div>
                  {!!step.requires_document && (
                    <div className="col-span-2">
                      <label className="block text-[7px] font-bold text-gray-400 uppercase mb-0.5">Document</label>
                      {step.file_url ? (
                        <div className="flex items-center gap-2 text-[8px]">
                          <a href={step.file_url} target="_blank" rel="noreferrer" className="text-gold hover:underline truncate max-w-[220px]">{step.file_name || 'View document'}</a>
                          <span className="text-gray-300">·</span>
                          <button onClick={() => save(step, { file_url: null, file_name: null })} className="text-red-500 hover:underline">remove</button>
                        </div>
                      ) : (
                        <FileUpload accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.heif,.doc,.docx"
                          label="Upload certificate / document"
                          onUpload={(r) => save(step, { file_url: r.url, file_name: r.filename })} />
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-2 flex items-center justify-between gap-2">
                <div className="text-[7px] text-gray-400">
                  {step.verified_at && <span className="text-green-600">Verified by {step.verified_by_name || '—'} on {formatDate(step.verified_at.slice(0, 10))}. </span>}
                  {step.updated_at && <span>Updated by {step.updated_by_name || '—'} on {formatDate(step.updated_at.slice(0, 10))}.</span>}
                </div>
                {buf[step.id] && (
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => clearBuf(step.id)} className="text-[8px] px-2 py-0.5 bg-gray-100 rounded">Cancel</button>
                    <button onClick={() => save(step)} disabled={savingId === step.id} className="text-[8px] px-2 py-0.5 bg-navy text-white rounded disabled:opacity-40">{savingId === step.id ? 'Saving…' : 'Save'}</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
